import React, { useState } from 'react';
import cx from 'classnames';

import _startCase from 'lodash/startCase';

import { Avatar, Tooltip } from '@trqk/components';

import { useModal } from '../../../../hooks';

import { InviteCollaborators } from '../../../modals';

interface Props {
    enableSendInvite?: boolean;
    name: string;
    percent_share: number;
    pro: string;
    title: string;
    trqkSongId: string;
}

export const OwnershipCard = ({
    enableSendInvite = false,
    name,
    percent_share,
    pro,
    title,
    trqkSongId,
}: Props): JSX.Element => {
    const modal = useModal();
    const [collaboratorsInvited, setCollaboratorsInvited] = useState(
        [] as Collaborator[],
    );
    const showCollaboratorsList =
        enableSendInvite && collaboratorsInvited.length > 0;
    const collaboratorList = collaboratorsInvited.map(
        (collaborator: Collaborator): string =>
            `${collaborator.firstName} ${collaborator.lastName}: ${collaborator.email}`,
    );
    const inviteOrInvites =
        collaboratorList.length === 1 ? 'invite' : 'invites';

    const InviteCollaboratorsModal = ({
        remove,
    }: ModalContentProps<Record<string, never>>): JSX.Element => (
        <InviteCollaborators
            handleInvitesSent={(collaborators: Collaborator[]): void => {
                setCollaboratorsInvited(
                    collaboratorsInvited.concat(collaborators),
                );
                remove({
                    success: true,
                    canceled: false,
                });
            }}
            remove={remove}
            title={title}
            trqkSongId={trqkSongId}
        />
    );

    return (
        <div
            className={cx('p-3', {
                'border border-quarternary-100 rounded': enableSendInvite,
            })}
            style={{
                display: 'grid',
                gridTemplateColumns: '50px auto',
            }}
        >
            <div
                style={{
                    alignSelf: 'center',
                }}
            >
                <Avatar gradientColor="secondary" name={name} />
            </div>
            <div
                style={{
                    alignSelf: 'left',
                    display: 'grid',
                    gridTemplateRows: 'auto auto auto',
                    gridTemplateColumns: 'auto auto',
                    alignItems: 'end',
                }}
            >
                <div
                    className="text-sm font-bold flex items-start"
                    style={{
                        gridColumn: '1',
                        gridRow: '1',
                    }}
                >
                    <div
                        className="truncate"
                        style={{
                            maxWidth: 170,
                        }}
                    >
                        {_startCase(name)}
                    </div>
                </div>
                {enableSendInvite && (
                    <div
                        style={{
                            gridArea: '1 / 2 / 2 / 2',
                        }}
                    >
                        <button
                            type="button"
                            className="ml-4 text-sm underline text-primary-400 font-semibold"
                            onClick={(): void => {
                                modal.add({
                                    Content: InviteCollaboratorsModal,
                                });
                            }}
                        >
                            Invite
                        </button>
                    </div>
                )}
                <div
                    className="text-sm flex items-start"
                    style={{
                        gridColumn: '1',
                        gridRow: '2',
                    }}
                >
                    {!enableSendInvite && <>{`${pro} ${percent_share}%`}</>}
                    {enableSendInvite && <>{`(${pro}) ${percent_share}%`}</>}
                </div>
                <div
                    style={{
                        gridColumn: '2',
                        gridRow: '2',
                    }}
                >
                    {showCollaboratorsList && (
                        <div className="ml-4 text-xs text-secondary-500 font-semibold">
                            <Tooltip
                                position="right"
                                message={collaboratorList.join('; ')}
                            >
                                {`${collaboratorsInvited.length} ${inviteOrInvites} sent`}
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
